import { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { EventContext } from '../event/eventContext';

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function getInAppBrowserName() {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';

  if (ua.includes('Instagram')) return 'Instagram';
  if (ua.includes('FBAN') || ua.includes('FBAV')) return 'Facebook';
  if (ua.includes('Messenger')) return 'Messenger';
  if (ua.toLowerCase().includes('tiktok')) return 'TikTok';
  if (ua.includes('LinkedInApp')) return 'LinkedIn';

  return null;
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
}

export default function InAppBrowserBanner() {
  const ev = useContext(EventContext);
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);

  const brandName = ev?.brand?.name || 'this app';
  const browserName = useMemo(() => getInAppBrowserName(), []);
  const show = useMemo(() => isIOS() && !!browserName && !dismissed, [browserName, dismissed]);

  const url = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return window.location.href;
  }, []);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  const handleCopyLink = async () => {
    const ok = await copyToClipboard(url);
    setCopied(ok);
  };

  if (!show) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top, 0px) + 10px)',
        left: 0,
        right: 0,
        zIndex: 2000,
        px: 1.5
      }}
    >
      <Paper
        elevation={6}
        sx={{
          borderRadius: 2,
          p: 1.5,
          maxWidth: 560,
          mx: 'auto'
        }}
      >
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'flex-start' }}>
          <Box sx={{ mt: 0.25 }}>
            <InfoOutlinedIcon fontSize="small" />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={0.75}>
              <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Open this in Safari
              </Typography>

              <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.35 }}>
                You are viewing this inside <b>{browserName}</b>. This browser does not share your Safari or {brandName} login.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ pt: 0.5 }}
              >
                <Button
                    fullWidth
                    variant="contained"
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ whiteSpace: 'nowrap' }}
                    >
                    Open in Safari
                    </Button>


                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleCopyLink}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
              </Stack>

              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Tip: If Safari does not open, tap the <b>•••</b> menu in the top right of Instagram and choose <b>Open in Browser</b>.
              </Typography>
            </Stack>
          </Box>

          <IconButton
            size="small"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            sx={{ mt: -0.5, mr: -0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Paper>
    </Box>
  );
}
